import { AUTO_TRIGGER_CONFIG_BY_KEY, AutoPopupKey } from "lib/constants/popupConfig";
import { AutoPopupEntry, AutoTriggerConfig, PopupEntry, PopupKey, PopupPayloadMap } from "lib/types/popup";
import { nanoid } from "nanoid";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";

type PopupSource = "MAIN" | "AUTO";

type OrchestratorState = {
  activePopup: PopupEntry | null;
  activeSource: PopupSource | null;
  mainQueue: PopupEntry[];
  autoQueue: AutoPopupEntry[];
  pausedMainEntry: PopupEntry | null;
  isLoadPhaseComplete: boolean;
  autoTriggerConfig: Record<AutoPopupKey, AutoTriggerConfig>;
};

type OrchestratorAction =
  | { type: "ENQUEUE_MAIN"; entry: PopupEntry }
  | { type: "OPEN_NESTED_MAIN"; entry: PopupEntry }
  | { type: "ENQUEUE_AUTO"; entry: AutoPopupEntry }
  | { type: "COMPLETE_LOAD_PHASE" }
  | { type: "ACTIVATE_NEXT_MAIN" }
  | { type: "ACTIVATE_NEXT_AUTO" }
  | { type: "INTERRUPT_WITH_AUTO" }
  | { type: "CLOSE" }
  | { type: "REMOVE"; id: string }
  | { type: "CLEAR_MAIN" }
  | { type: "SET_AUTO_TRIGGER_CONFIG"; config: Record<AutoPopupKey, AutoTriggerConfig> };

const initialState: OrchestratorState = {
  activePopup: null,
  activeSource: null,
  mainQueue: [],
  autoQueue: [],
  pausedMainEntry: null,
  isLoadPhaseComplete: false,
  autoTriggerConfig: AUTO_TRIGGER_CONFIG_BY_KEY,
};

function insertAutoEntry(queue: AutoPopupEntry[], entry: AutoPopupEntry): AutoPopupEntry[] {
  const insertIndex = queue.findIndex((queued) => queued.weight > entry.weight);
  if (insertIndex === -1) {
    return [...queue, entry];
  }
  return [...queue.slice(0, insertIndex), entry, ...queue.slice(insertIndex)];
}

function syncAutoQueueWithConfig(
  queue: AutoPopupEntry[],
  config: Record<AutoPopupKey, AutoTriggerConfig>,
): AutoPopupEntry[] {
  return queue
    .filter((entry) => config[entry.key as AutoPopupKey]?.enabled !== false)
    .map((entry) => ({
      ...entry,
      weight: config[entry.key as AutoPopupKey]?.weight ?? entry.weight,
    }))
    .sort((a, b) => a.weight - b.weight);
}

function orchestratorReducer(state: OrchestratorState, action: OrchestratorAction): OrchestratorState {
  switch (action.type) {
    case "ENQUEUE_MAIN": {
      if (state.activeSource === "AUTO") {
        return {
          ...state,
          activePopup: action.entry,
          activeSource: "MAIN",
        };
      }
      if (state.activePopup === null) {
        return {
          ...state,
          activePopup: action.entry,
          activeSource: "MAIN",
        };
      }
      return {
        ...state,
        mainQueue: [...state.mainQueue, action.entry],
      };
    }

    // Opens a popup on top of whatever's currently active without dropping it - pauses the
    // active entry (same slot INTERRUPT_WITH_AUTO uses) instead of closing it, so CLOSE
    // restores it once the nested popup is dismissed. Used for popups like ITEM_PROPERTIES
    // that can be triggered from inside another already-open popup (e.g. OOSDialog).
    case "OPEN_NESTED_MAIN": {
      if (state.activePopup === null) {
        return { ...state, activePopup: action.entry, activeSource: "MAIN" };
      }
      return {
        ...state,
        pausedMainEntry: state.activePopup,
        activePopup: action.entry,
        activeSource: "MAIN",
      };
    }

    case "ENQUEUE_AUTO": {
      const { entry } = action;

      if (state.activePopup?.key === entry.key && state.activeSource === "AUTO") {
        return state;
      }

      if (state.autoQueue.some((queued) => queued.key === entry.key)) {
        return state;
      }

      return {
        ...state,
        autoQueue: insertAutoEntry(state.autoQueue, entry),
      };
    }

    case "COMPLETE_LOAD_PHASE": {
      if (state.isLoadPhaseComplete) {
        return state;
      }
      return {
        ...state,
        isLoadPhaseComplete: true,
        autoQueue: syncAutoQueueWithConfig(state.autoQueue, state.autoTriggerConfig),
      };
    }

    case "ACTIVATE_NEXT_MAIN": {
      if (state.activePopup !== null || state.mainQueue.length === 0) {
        return state;
      }
      const [next, ...rest] = state.mainQueue;
      return {
        ...state,
        activePopup: next,
        activeSource: "MAIN",
        mainQueue: rest,
      };
    }

    case "ACTIVATE_NEXT_AUTO": {
      if (state.activePopup !== null || state.mainQueue.length > 0 || state.autoQueue.length === 0) {
        return state;
      }
      const [next, ...rest] = state.autoQueue;
      return {
        ...state,
        activePopup: next,
        activeSource: "AUTO",
        autoQueue: rest,
      };
    }

    case "INTERRUPT_WITH_AUTO": {
      if (state.activePopup === null || state.activeSource !== "MAIN" || state.autoQueue.length === 0) {
        return state;
      }
      const nextAuto = state.autoQueue[0];
      const config = state.autoTriggerConfig[nextAuto.key as AutoPopupKey];
      if (!config?.interruptable) {
        return state;
      }
      return {
        ...state,
        pausedMainEntry: state.activePopup,
        activePopup: nextAuto,
        activeSource: "AUTO",
        autoQueue: state.autoQueue.slice(1),
      };
    }

    case "CLOSE": {
      if (state.pausedMainEntry !== null) {
        return {
          ...state,
          activePopup: state.pausedMainEntry,
          activeSource: "MAIN",
          pausedMainEntry: null,
        };
      }

      return {
        ...state,
        activePopup: null,
        activeSource: null,
      };
    }

    case "REMOVE": {
      if (state.activePopup?.id === action.id) {
        return orchestratorReducer(state, { type: "CLOSE" });
      }

      return {
        ...state,
        mainQueue: state.mainQueue.filter((entry) => entry.id !== action.id),
        autoQueue: state.autoQueue.filter((entry) => entry.id !== action.id),
      };
    }

    case "CLEAR_MAIN": {
      return {
        ...state,
        mainQueue: [],
      };
    }

    case "SET_AUTO_TRIGGER_CONFIG": {
      return {
        ...state,
        autoTriggerConfig: action.config,
        autoQueue: syncAutoQueueWithConfig(state.autoQueue, action.config),
      };
    }

    default:
      return state;
  }
}

export interface PopupOrchestratorContextValue {
  activePopup: PopupEntry | null;
  activeSource: PopupSource | null;
  mainQueue: PopupEntry[];
  autoQueue: AutoPopupEntry[];
  pausedMainEntry: PopupEntry | null;
  isLoadPhaseComplete: boolean;
  autoTriggerConfig: Record<AutoPopupKey, AutoTriggerConfig>;
  openPopup: <K extends PopupKey>(
    key: K,
    payload: PopupPayloadMap[K],
    callbacks?: {
      onClose?: (confirmed: boolean) => void;
      /** Pause the currently active popup instead of closing it, and restore it once this one closes. */
      nested?: boolean;
    },
  ) => void;
  enqueueAuto: <K extends AutoPopupKey>(
    key: K,
    payload: PopupPayloadMap[K],
    callbacks?: { onClose?: (confirmed: boolean) => void },
  ) => void;
  completeLoadPhase: () => void;
  closePopup: (confirmed?: boolean) => void;
  removeFromQueue: (id: string) => void;
  clearMainQueue: () => void;
  setAutoTriggerConfig: (config: Record<AutoPopupKey, AutoTriggerConfig>) => void;
}

const PopupOrchestratorContext = createContext<PopupOrchestratorContextValue>(
  {} as PopupOrchestratorContextValue,
);

export const PopupOrchestratorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(orchestratorReducer, initialState);
  const pendingActivationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    return () => {
      if (pendingActivationTimer.current !== null) {
        clearTimeout(pendingActivationTimer.current);
      }
    };
  }, []);

  const clearPendingActivationTimer = useCallback(() => {
    if (pendingActivationTimer.current !== null) {
      clearTimeout(pendingActivationTimer.current);
      pendingActivationTimer.current = null;
    }
  }, []);

  const tryScheduleActivation = useCallback(() => {
    const currentState = stateRef.current;

    if (!currentState.isLoadPhaseComplete) {
      return;
    }

    if (currentState.activePopup !== null) {
      if (currentState.activeSource === "MAIN" && currentState.autoQueue.length > 0) {
        const frontAuto = currentState.autoQueue[0];
        const config = currentState.autoTriggerConfig[frontAuto.key as AutoPopupKey];
        if (config?.interruptable) {
          clearPendingActivationTimer();
          dispatch({ type: "INTERRUPT_WITH_AUTO" });
        }
      }
      return;
    }

    if (currentState.mainQueue.length > 0) {
      clearPendingActivationTimer();
      dispatch({ type: "ACTIVATE_NEXT_MAIN" });
      return;
    }

    if (currentState.autoQueue.length === 0) {
      return;
    }

    if (pendingActivationTimer.current !== null) {
      return;
    }

    const nextAuto = currentState.autoQueue[0];
    const config = currentState.autoTriggerConfig[nextAuto.key as AutoPopupKey];
    const delay = config?.delay ?? 0;

    const activate = () => {
      pendingActivationTimer.current = null;
      dispatch({ type: "ACTIVATE_NEXT_AUTO" });
    };

    if (delay > 0) {
      pendingActivationTimer.current = setTimeout(activate, delay);
      return;
    }

    activate();
  }, [clearPendingActivationTimer]);

  useEffect(() => {
    tryScheduleActivation();
  }, [state.autoQueue, state.mainQueue, state.activePopup, state.isLoadPhaseComplete, tryScheduleActivation]);

  const openPopup = useCallback<PopupOrchestratorContextValue["openPopup"]>(
    (key, payload, callbacks) => {
      const config = stateRef.current.autoTriggerConfig[key as AutoPopupKey];

      if (key in stateRef.current.autoTriggerConfig && !config.enabled) {
        return;
      }
      clearPendingActivationTimer();
      const entry: PopupEntry = {
        id: nanoid(),
        key,
        payload,
        onClose: callbacks?.onClose,
      };
      if (callbacks?.nested) {
        dispatch({ type: "OPEN_NESTED_MAIN", entry });
        return;
      }
      dispatch({ type: "CLOSE" });
      dispatch({ type: "ENQUEUE_MAIN", entry });
    },
    [clearPendingActivationTimer],
  );

  const enqueueAuto = useCallback<PopupOrchestratorContextValue["enqueueAuto"]>((key, payload, callbacks) => {
    const config = stateRef.current.autoTriggerConfig[key];

    if (!config?.enabled) {
      return;
    }

    const entry: AutoPopupEntry = {
      id: nanoid(),
      key,
      payload,
      weight: config.weight,
      onClose: callbacks?.onClose,
    };

    dispatch({ type: "ENQUEUE_AUTO", entry });
  }, []);

  const completeLoadPhase = useCallback(() => {
    dispatch({ type: "COMPLETE_LOAD_PHASE" });
  }, []);

  const closePopup = useCallback(
    (confirmed = false) => {
      clearPendingActivationTimer();
      dispatch({ type: "CLOSE" });
      stateRef.current.activePopup?.onClose?.(confirmed);
    },
    [clearPendingActivationTimer],
  );

  const removeFromQueue = useCallback(
    (id: string) => {
      clearPendingActivationTimer();

      if (stateRef.current.activePopup?.id === id) {
        stateRef.current.activePopup.onClose?.(false);
      }

      dispatch({ type: "REMOVE", id });
    },
    [clearPendingActivationTimer],
  );

  const clearMainQueue = useCallback(() => {
    dispatch({ type: "CLEAR_MAIN" });
  }, []);

  const setAutoTriggerConfig = useCallback((config: Record<AutoPopupKey, AutoTriggerConfig>) => {
    dispatch({ type: "SET_AUTO_TRIGGER_CONFIG", config });
  }, []);

  const value = useMemo<PopupOrchestratorContextValue>(
    () => ({
      activePopup: state.activePopup,
      activeSource: state.activeSource,
      mainQueue: state.mainQueue,
      autoQueue: state.autoQueue,
      pausedMainEntry: state.pausedMainEntry,
      isLoadPhaseComplete: state.isLoadPhaseComplete,
      autoTriggerConfig: state.autoTriggerConfig,
      openPopup,
      enqueueAuto,
      completeLoadPhase,
      closePopup,
      removeFromQueue,
      clearMainQueue,
      setAutoTriggerConfig,
    }),
    [
      state.activePopup,
      state.activeSource,
      state.mainQueue,
      state.autoQueue,
      state.pausedMainEntry,
      state.isLoadPhaseComplete,
      state.autoTriggerConfig,
      openPopup,
      enqueueAuto,
      completeLoadPhase,
      closePopup,
      removeFromQueue,
      clearMainQueue,
      setAutoTriggerConfig,
    ],
  );

  return <PopupOrchestratorContext.Provider value={value}>{children}</PopupOrchestratorContext.Provider>;
};

export const usePopupOrchestrator = (): PopupOrchestratorContextValue => {
  const context = useContext(PopupOrchestratorContext);
  if (!context.openPopup) {
    throw new Error("usePopupOrchestrator must be used within PopupOrchestratorProvider");
  }
  return context;
};
